import { useMemo, useState } from 'react';
import Section from '@widgets/components/section';
import { Table, TableBody, TableTd, TableTh, TableThead, TableTr } from '@widgets/table';
import { formatNum } from '@app/utils/math';
import ProgressBar from '@shared/uikit/progress-bar';
import { TextSize, Typography } from '@shared/index';
import { Pencil } from 'lucide-react';
import clsx from 'clsx';
import StatusBadge, { STATUS } from '@shared/uikit/status-badge';
import { Loader } from '@shared/uikit/loader';
import { toast } from 'react-toastify';
import { daysUntilEndOfYear } from '@app/utils/date';
import { useQueryClient } from '@tanstack/react-query';
import { EditYearlyResult } from '../progress-card/edit-yearly-result';
import {
  useGetManagersYearlyPlanList,
  useGetTeamYearlyBonusResults,
  useUpdateTeamYearlyPlan,
} from '@entities/managers-perfomance';
import { QUERY_KEY_MANAGER_PERFORMANCE } from '@entities/managers-perfomance/query-keys';

type Props = {
  year?: number;
};

export const YearlyResult = ({ year = new Date().getFullYear() }: Props) => {
  const daysRemaining = daysUntilEndOfYear();
  const [isEditBonusPopup, setIsEditBonusPopup] = useState(false);
  const queryClient = useQueryClient();

  const { data: teamYearlyResults = [], isLoading: isLoadingResults } = useGetTeamYearlyBonusResults({ year });
  const { data: managersYearlyPlanList, isLoading: isLoadingPlan } = useGetManagersYearlyPlanList(year);
  const updateTeamYearlyPlan = useUpdateTeamYearlyPlan();

  const isLoading = isLoadingResults || isLoadingPlan;

  const currentPlan = managersYearlyPlanList?.find(plan => plan.year === year) || managersYearlyPlanList?.[0];
  const bonusPlanValue = currentPlan?.year_plan ? formatNum(currentPlan.year_plan) : '';
  const bonusAmount = currentPlan?.year_plan ?? 0;

  const handleOpenEditPopup = () => {
    setIsEditBonusPopup(true);
  };

  const handleCloseEditPopup = () => {
    setIsEditBonusPopup(false);
  };

  const handleUpdateBonusPlan = async (plan: string) => {
    const cleanPlan = plan.replace(/\s/g, '') || '-';

    const bonusManager =
      currentPlan?.is_bonus_active && currentPlan?.bonus_manager_id
        ? currentPlan.bonus_manager_id
        : null;

    const requestData = {
      year,
      year_plan: cleanPlan,
      is_bonus_active: currentPlan?.is_bonus_active ?? false,
      bonus_manager: bonusManager,
    };

    await updateTeamYearlyPlan.updateTeamYearlyPlan(requestData);

    await queryClient.invalidateQueries({
      queryKey: [QUERY_KEY_MANAGER_PERFORMANCE.MANAGERS_YEARLY_PLAN_LIST, year],
    });
    toast.success('Годовой план команды обновлен');
    setIsEditBonusPopup(false);
  };

  const tableData = useMemo(() => {
    if (!teamYearlyResults.length || !currentPlan) return [];

    const bonusTarget = currentPlan.year_plan || 0;

    return teamYearlyResults.map(({ manager_id, manager_name, hashrate }) => {
      const done_hashrate = hashrate;
      const remaining_hashrate = Math.max(bonusTarget - done_hashrate, 0);
      const completion_percentage = bonusTarget > 0 ? (done_hashrate / bonusTarget) * 100 : 0;
      const status = done_hashrate >= bonusTarget ? 'completed' : 'in_progress';

      return {
        managerId: manager_id,
        name: manager_name,
        done: done_hashrate,
        remaining: remaining_hashrate,
        percent: completion_percentage,
        status: status as 'completed' | 'in_progress',
      };
    });
  }, [teamYearlyResults, currentPlan]);

  if (isLoading) {
    return (
      <Section>
        <Loader loading={true} />
      </Section>
    );
  }

  return (
    <Section>
      <div className="flex items-center justify-between w-full mb-3">
        <Typography
          size={TextSize.size24}
          title="Показатели менеджеров за год"
          className="font-semibold"
        />
        <div className="flex items-baseline gap-10">
          <div className="flex items-center gap-10">
            <span className="text-xs font-medium text-gray-400">Осталось: </span>
            <span className="text-[20px] text-white">
              {daysRemaining}
              <span className="text-xs font-medium text-gray-400 align-top ml-[6px]">дн</span>
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-400">План: </span>
            <span className="rounded-full border-none bg-green-400/10 border flex items-center px-3 py-1 text-sm text-green-400">
              {formatNum(bonusAmount)} Ph
            </span>
            <Pencil
              size={16}
              className="text-gray-400 cursor-pointer hover:opacity-70"
              onClick={handleOpenEditPopup}
            />
          </div>
        </div>
      </div>
      {tableData.length === 0 ? (
        <div className="flex items-center justify-center py-8">
          <Typography
            size={TextSize.size16}
            title="Нет данных для отображения"
            className="text-gray-400"
          />
        </div>
      ) : (
        <div className="overflow-y-auto max-h-[410px]">
          <Table>
            <TableThead className="text-white/40 [&_div]:justify-start">
              <TableTr withoutUnderline>
                <TableTh title="Имя" className="w-[20%]" />
                <TableTh title="Прогресс" className="w-[25%]" />
                <TableTh title="Сделано, Ph" className="w-[15%]" />
                <TableTh title="Осталось, Ph" className="w-[15%]" />
                <TableTh title="% выполнения" className="w-[15%]" />
                <TableTh title="Статус" className="w-[10%]" />
              </TableTr>
            </TableThead>
            <TableBody>
              {tableData.map((item, index) => {
                const isOdd = index % 2 === 0;
                const isCompleted = item.percent >= 100;
                const progressBarColor = isCompleted
                  ? '#71DD8C'
                  : item.status === 'completed'
                    ? '#71DD8C'
                    : '#519EFB';
                const percentColorClass = isCompleted
                  ? ''
                  : item.status === 'completed'
                    ? 'text-green-200'
                    : 'text-blue-300';
                const percentColorStyle = isCompleted ? { color: '#71DD8C' } : undefined;

                return (
                  <TableTr
                    key={item.managerId}
                    style={{ backgroundColor: isOdd ? '#1D1D1D' : 'unset' }}
                    withoutUnderline
                  >
                    <TableTd
                      title={
                        <div className="flex items-center gap-2">
                          <span>{item.name}</span>
                        </div>
                      }
                      className={clsx(isOdd && 'rounded-l-[16px]')}
                    />
                    <TableTd
                      title={
                        <div className="w-full">
                          <ProgressBar
                            className="h-[10px]"
                            percent={item.percent}
                            fillerColor={progressBarColor}
                          />
                        </div>
                      }
                    />
                    <TableTd title={`${formatNum(item.done)}`} />
                    <TableTd title={`${formatNum(item.remaining)}`} />
                    <TableTd
                      title={
                        <span className={percentColorClass} style={percentColorStyle}>
                          {Math.round(item.percent)}%
                        </span>
                      }
                    />
                    <TableTd
                      title={
                        <StatusBadge
                          status={
                            isCompleted || item.status === 'completed'
                              ? STATUS.COMPLETED
                              : STATUS.IN_PROGRESS
                          }
                        />
                      }
                      className={clsx(isOdd && 'rounded-r-[16px]')}
                    />
                  </TableTr>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
      {isEditBonusPopup && (
        <EditYearlyResult
          open={true}
          value={bonusPlanValue}
          onSubmit={handleUpdateBonusPlan}
          onClose={handleCloseEditPopup}
        />
      )}
    </Section>
  );
};